import { format } from "date-fns";
import { ISourceDocumentCreateGuiModel, ISourceDocumentGuiModel, ISourceDocumentGuiModifyModel, ISourceDocumentMarkCompletedGuiModel, ISourceDocumentTypeStatisticsGuiModel } from "../model/ISourceDocumentGuiModel";
import SourceDocumentService from "../service/SourceDocumentService";
import { IFileSortItem, ISourceDocumentModel, ISourceDocumentModifyModel } from "../service/model/ISourceDocumentModel";
import { BusinessLogicDefaultResult, BusinessLogicResult, PageBusinessLogicResult } from "./BusinessLogicResult";
import { toast } from "react-toastify";
import { digiAccGuiStates, digiAccGuiTypes, digiAccTypes } from "../common/TextTypes";

export default class SourceDocumentBusinessLogic {

    public async getSourceDocuments(
        organizationId?: string,
        page?: number,
        limit?: number,
        voucherDateFrom?: Date,
        voucherDateTo?: Date,
        dueDateFrom?: Date,
        dueDateTo?: Date,
        valueDateFrom?: Date,
        valueDateTo?: Date,
        createdDateFrom?: Date,
        createdDateTo?: Date,
        voucherStatus?: digiAccGuiStates[],
        voucherType?: digiAccGuiTypes,
        invoiceNo?: string,
        debitAccount?: number,
        creditAccount?: number,
        departmentId?: string,
        projectId?: string,
        voucherIdFrom?: string,
        voucherIdTo?: string,
        amountFrom?: string,
        amountTo?: string,
        freeTextSearch?: string,
        sortDocumentField?: IFileSortItem,
    ): Promise<PageBusinessLogicResult<ISourceDocumentGuiModel[]>> {

        const service = new SourceDocumentService();
        const result = await service.getSourceDocuments(
            organizationId,
            page,
            limit,
            voucherDateFrom,
            voucherDateTo,
            dueDateFrom,
            dueDateTo,
            valueDateFrom,
            valueDateTo,
            createdDateFrom,
            createdDateTo,
            voucherStatus,
            voucherType,
            invoiceNo,
            debitAccount,
            creditAccount,
            departmentId,
            projectId,
            voucherIdFrom,
            voucherIdTo,
            amountFrom,
            amountTo,
            freeTextSearch,
            sortDocumentField,
        );

        if (result.hasErrors) {
            return new PageBusinessLogicResult([], 0, 0, 0, result.errors);
        } else {
            let apiData: ISourceDocumentModel[] = result.value;

            let data: ISourceDocumentGuiModel[] = apiData.map(e => {
                return {
                    id: e.id,
                    batchId: e.batchId ? e.batchId : undefined,
                    type: e.type,
                    comments: e.comments ? e.comments : undefined,
                    status: e.status,
                    createdDate: e.createdDate ? new Date(e.createdDate) : null,
                    createdBy: e.createdBy ? e.createdBy : undefined,
                    modifiedDate: e.modifiedDate ? new Date(e.modifiedDate) : null,
                    modifiedBy: e.modifiedBy ? e.modifiedBy : undefined,
                    originalSourceDocumentId: e.originalSourceDocumentId ? e.originalSourceDocumentId : undefined,
                    correctionComments: e.correctionComments ? e.correctionComments : undefined,
                    voucherId: e.voucherId ? e.voucherId : undefined,
                    source: e.source,
                    lines: e.lines ? e.lines.map(l => {
                        return {
                            id: l.id ? l.id : undefined,
                            invoiceNo: l.invoiceNo ? l.invoiceNo : undefined,
                            debitAccount: l.debitAccount ? {
                                accountId: l.debitAccount.accountId ? l.debitAccount.accountId.toString() : undefined,
                                name: l.debitAccount.name ? l.debitAccount.name : undefined,
                                accountType: l.debitAccount.accountType,
                                bankAccount: l.debitAccount.bankAccount ? l.debitAccount.bankAccount : undefined,
                                businessNumber: l.debitAccount.businessNumber ? l.debitAccount.businessNumber : undefined,
                                vatCode: l.debitAccount.vatCode ? l.debitAccount.vatCode.toString() : undefined
                            } : undefined,
                            debitVat: l.debitVat ? {
                                vatCodeId: l.debitVat.vatCodeId.toString(),
                                name: l.debitVat.name ? l.debitVat.name : undefined,
                                rate: l.debitVat.rate,
                                turnoverClass: l.debitVat.turnoverClass ? l.debitVat.turnoverClass : undefined
                            } : undefined,
                            creditAccount: l.creditAccount ? {
                                accountId: l.creditAccount.accountId ? l.creditAccount.accountId.toString() : undefined,
                                name: l.creditAccount.name ? l.creditAccount.name : undefined,
                                accountType: l.creditAccount.accountType,
                                bankAccount: l.creditAccount.bankAccount ? l.creditAccount.bankAccount : undefined,
                                businessNumber: l.creditAccount.businessNumber ? l.creditAccount.businessNumber : undefined,
                                vatCode: l.creditAccount.vatCode ? l.creditAccount.vatCode.toString() : undefined
                            } : undefined,
                            creditVat: l.creditVat ? {
                                vatCodeId: l.creditVat.vatCodeId.toString(),
                                name: l.creditVat.name ? l.creditVat.name : undefined,
                                rate: l.creditVat.rate,
                                turnoverClass: l.creditVat.turnoverClass ? l.creditVat.turnoverClass : undefined
                            } : undefined,
                            amount: l.amount ? l.amount : undefined,
                            voucherDate: l.voucherDate ? new Date(l.voucherDate) : null,
                            dueDate: l.dueDate ? new Date(l.dueDate) : null,
                            valueDate: l.valueDate ? new Date(l.valueDate) : null,
                            project: l.project ? {
                                projectId: l.project.projectId,
                                name: l.project.name ? l.project.name : undefined
                            } : undefined,
                            department: l.department ? {
                                departmentId: l.department.departmentId,
                                name: l.department.name ? l.department.name : undefined
                            } : undefined,
                            text: l.text ? l.text : undefined,
                            crossReference: l.crossReference ? l.crossReference : undefined,
                            cid: l.cid ? l.cid : undefined,
                            currency: l.currency ? l.currency : undefined,
                            currencyVismaId: l.currencyVismaId ? l.currencyVismaId : undefined,
                            currencyRate: l.currencyRate ? l.currencyRate : undefined,
                            currencyAmount: l.currencyAmount ? l.currencyAmount : undefined,
                            currencyDate: l.currencyDate ? new Date(l.currencyDate) : null
                        }
                    }) : undefined,
                    approvalRequests: e.approvalRequests ? e.approvalRequests : undefined,
                    files: e.files ? e.files.map(f => {
                        return {
                            id: f.id,
                            fileName: f.fileName ? f.fileName : undefined,
                            pages: f.pages,
                            isPrimary: f.isPrimary,
                            isPreviewReady: f.isPreviewReady
                        }
                    }) : undefined,
                }
            });

            return new PageBusinessLogicResult(data, result.currentPage, result.totalPages, result.totalItems, result.errors);

        }

    };

    public async getSourceDocumentsExcelFile(
        organizationId?: string,
        page?: number,
        limit?: number,
        voucherDateFrom?: Date,
        voucherDateTo?: Date,
        dueDateFrom?: Date,
        dueDateTo?: Date,
        valueDateFrom?: Date,
        valueDateTo?: Date,
        createdDateFrom?: Date,
        createdDateTo?: Date,
        voucherStatus?: digiAccGuiStates[],
        voucherType?: digiAccGuiTypes,
        invoiceNo?: string,
        debitAccount?: number,
        creditAccount?: number,
        departmentId?: string,
        projectId?: string,
        voucherIdFrom?: string,
        voucherIdTo?: string,
        amountFrom?: string,
        amountTo?: string,
        freeTextSearch?: string,
        sortDocumentField?: IFileSortItem,
    ): Promise<BusinessLogicResult<Blob | undefined>> {

        const service = new SourceDocumentService();
        const result = await service.getSourceDocumentsExcelFile(
            organizationId,
            page,
            limit,
            voucherDateFrom,
            voucherDateTo,
            dueDateFrom,
            dueDateTo,
            valueDateFrom,
            valueDateTo,
            createdDateFrom,
            createdDateTo,
            voucherStatus,
            voucherType,
            invoiceNo,
            debitAccount,
            creditAccount,
            departmentId,
            projectId,
            voucherIdFrom,
            voucherIdTo,
            amountFrom,
            amountTo,
            freeTextSearch,
            sortDocumentField,
        );

        if (result.hasErrors) {
            return new BusinessLogicResult(undefined, result.errors);
        } else {
            return new BusinessLogicResult(result.value, result.errors);

        }

    };



    public async getSourceDocument(organizationId: string, sourceDocumentId: number, loadExternalData: boolean = true): Promise<BusinessLogicResult<ISourceDocumentGuiModel>> {

        const service = new SourceDocumentService();

        const result = await service.getSourceDocument(organizationId, sourceDocumentId, loadExternalData);

        const e = result.value;

        const data: ISourceDocumentGuiModel = {
            id: e.id,
            batchId: e.batchId ? e.batchId : undefined,
            type: e.type,
            comments: e.comments ? e.comments : undefined,
            status: e.status,
            createdDate: e.createdDate ? new Date(e.createdDate) : null,
            createdBy: e.createdBy ? e.createdBy : undefined,
            modifiedDate: e.modifiedDate ? new Date(e.modifiedDate) : null,
            modifiedBy: e.modifiedBy ? e.modifiedBy : undefined,
            originalSourceDocumentId: e.originalSourceDocumentId ? e.originalSourceDocumentId : undefined,
            correctionComments: e.correctionComments ? e.correctionComments : undefined,
            voucherId: e.voucherId ? e.voucherId : undefined,
            source: e.source,
            lines: e.lines ? e.lines.map(l => {
                return {
                    id: l.id ? l.id : undefined,
                    invoiceNo: l.invoiceNo ? l.invoiceNo : undefined,
                    debitAccount: l.debitAccount ? {
                        accountId: l.debitAccount.accountId ? l.debitAccount.accountId.toString() : undefined,
                        name: l.debitAccount.name ? l.debitAccount.name : undefined,
                        accountType: l.debitAccount.accountType,
                        bankAccount: l.debitAccount.bankAccount ? l.debitAccount.bankAccount : undefined,
                        businessNumber: l.debitAccount.businessNumber ? l.debitAccount.businessNumber : undefined,
                        vatCode: l.debitAccount.vatCode ? l.debitAccount.vatCode.toString() : undefined
                    } : undefined,
                    debitVat: l.debitVat ? {
                        vatCodeId: l.debitVat.vatCodeId.toString(),
                        name: l.debitVat.name ? l.debitVat.name : undefined,
                        rate: l.debitVat.rate,
                        turnoverClass: l.debitVat.turnoverClass ? l.debitVat.turnoverClass : undefined
                    } : undefined,
                    creditAccount: l.creditAccount ? {
                        accountId: l.creditAccount.accountId ? l.creditAccount.accountId.toString() : undefined,
                        name: l.creditAccount.name ? l.creditAccount.name : undefined,
                        accountType: l.creditAccount.accountType,
                        bankAccount: l.creditAccount.bankAccount ? l.creditAccount.bankAccount : undefined,
                        businessNumber: l.creditAccount.businessNumber ? l.creditAccount.businessNumber : undefined,
                        vatCode: l.creditAccount.vatCode ? l.creditAccount.vatCode.toString() : undefined
                    } : undefined,
                    creditVat: l.creditVat ? {
                        vatCodeId: l.creditVat.vatCodeId.toString(),
                        name: l.creditVat.name ? l.creditVat.name : undefined,
                        rate: l.creditVat.rate,
                        turnoverClass: l.creditVat.turnoverClass ? l.creditVat.turnoverClass : undefined
                    } : undefined,
                    amount: l.amount ? l.amount : undefined,
                    voucherDate: l.voucherDate ? new Date(l.voucherDate) : null,
                    dueDate: l.dueDate ? new Date(l.dueDate) : null,
                    valueDate: l.valueDate ? new Date(l.valueDate) : null,
                    project: l.project ? {
                        projectId: l.project.projectId,
                        name: l.project.name ? l.project.name : undefined
                    } : undefined,
                    department: l.department ? {
                        departmentId: l.department.departmentId,
                        name: l.department.name ? l.department.name : undefined
                    } : undefined,
                    text: l.text ? l.text : undefined,
                    crossReference: l.crossReference ? l.crossReference : undefined,
                    cid: l.cid ? l.cid : undefined,
                    currency: l.currency ? l.currency : undefined,
                    currencyVismaId: l.currencyVismaId ? l.currencyVismaId : undefined,
                    currencyRate: l.currencyRate ? l.currencyRate : undefined,
                    currencyAmount: l.currencyAmount ? l.currencyAmount : undefined,
                    currencyDate: l.currencyDate ? new Date(l.currencyDate) : null
                }
            }) : undefined,
            approvalRequests: e.approvalRequests ? e.approvalRequests : undefined,
            files: e.files ? e.files.map(f => {
                return {
                    id: f.id,
                    fileName: f.fileName ? f.fileName : undefined,
                    pages: f.pages,
                    isPrimary: f.isPrimary,
                    isPreviewReady: f.isPreviewReady
                }
            }) : undefined,
        }

        return new BusinessLogicResult(data, result.errors);
    }

    public async postSourceDocumentCreateAddition(
        organizationId: string, sourceDocumentIds: number[],
    ): Promise<BusinessLogicDefaultResult> {

        const service = new SourceDocumentService();

        const result = await service.postSourceDocumentsCreateAddition(organizationId, sourceDocumentIds);

        if (result.hasErrors) {
            return new BusinessLogicDefaultResult(result.errors);
        } else {
            return new BusinessLogicDefaultResult([]);
        }

    }

    public async postSourceDocumentTransfer(
        organizationId: string, sourceDocumentIds: number[],
    ): Promise<BusinessLogicDefaultResult> {

        const service = new SourceDocumentService();

        const result = await service.postSourceDocumentsTransfer(organizationId, sourceDocumentIds);

        if (result.hasErrors) {
            return new BusinessLogicDefaultResult(result.errors);
        } else {
            return new BusinessLogicDefaultResult([]);
        }

    }

    public async postSourceDocumentTransferAll(
        organizationId: string, type: "IncomingInvoice" | "OutgoingInvoice" | "Banking" | "JournalEntries" | "Miscellaneous" | "Addition" | "Cash" | "Statement",
    ): Promise<BusinessLogicDefaultResult> {

        const service = new SourceDocumentService();

        const result = await service.postSourceDocumentsTransferAll(organizationId, type);

        if (result.hasErrors) {
            return new BusinessLogicDefaultResult(result.errors);
        } else {
            return new BusinessLogicDefaultResult([]);
        }

    }

    public async postSourceDocumentReject(
        organizationId: string, sourceDocumentIds: number[],
    ): Promise<BusinessLogicDefaultResult> {

        const service = new SourceDocumentService();

        const result = await service.postSourceDocumentReject(organizationId, sourceDocumentIds);

        if (result.hasErrors) {
            return new BusinessLogicDefaultResult(result.errors);
        } else {
            return new BusinessLogicDefaultResult([]);
        }

    }

    public async postSourceDocumentMarkCompleted(
        organizationId: string, sourceDocumentId: number, ignoreWarnings: boolean
    ): Promise<BusinessLogicResult<ISourceDocumentMarkCompletedGuiModel[]>> {

        const service = new SourceDocumentService();

        const result = await service.postSourceDocumentMarkCompleted(organizationId, sourceDocumentId, ignoreWarnings);

        if (result.hasErrors) {
            return new BusinessLogicResult([],result.errors);
        } else {
            return new BusinessLogicResult(result.value ? result.value : [], []);
        }

    }

    public async postSourceDocumentUndoReject(
        organizationId: string, sourceDocumentIds: number[],
    ): Promise<BusinessLogicDefaultResult> {

        const service = new SourceDocumentService();

        const result = await service.postSourceDocumentUndoReject(organizationId, sourceDocumentIds);

        if (result.hasErrors) {
            return new BusinessLogicDefaultResult(result.errors);
        } else {
            return new BusinessLogicDefaultResult([]);
        }

    }

    public async postSourceDocumentMove(
        organizationId: string, sourceDocumentIds: number[], type: "IncomingInvoice" | "OutgoingInvoice" | "Banking" | "JournalEntries" | "Miscellaneous" | "Addition" | "Cash" | "Statement"
    ): Promise<BusinessLogicDefaultResult> {

        const service = new SourceDocumentService();

        const result = await service.postSourceDocumentMove(organizationId, sourceDocumentIds, type);

        if (result.hasErrors) {
            return new BusinessLogicDefaultResult(result.errors);
        } else {
            return new BusinessLogicDefaultResult([]);
        }

    }

    public async postSourceDocumentFile(organizationId: string, sourceDocumentId: number, file: File): Promise<BusinessLogicDefaultResult> {

        const service = new SourceDocumentService();

        const result = await service.postSourceDocumentFile(organizationId, sourceDocumentId, file);

        if (result.hasErrors) {
            return new BusinessLogicDefaultResult(result.errors);
        } else {
            return new BusinessLogicDefaultResult([]);
        }
    }

    public async postSourceDocument(organizationId: string, sourceDocumentType: digiAccTypes, comment: string, fileContents: string, data: ISourceDocumentGuiModifyModel[]): 
    Promise<BusinessLogicResult<ISourceDocumentCreateGuiModel | undefined>> {

        const service = new SourceDocumentService();

        const setLines: ISourceDocumentModifyModel[] = data.map(e => {
            return {
                invoiceNo: e.invoiceNo,
                debitAccount: e.debitAccount ? parseInt(e.debitAccount) : null,
                debitAccountType: e.debitAccountType,
                debitVAT: e.debitVAT ? parseInt(e.debitVAT) : null,
                creditAccount: e.creditAccount ? parseInt(e.creditAccount) : null,
                creditAccountType: e.creditAccountType,
                creditVAT: e.creditVAT ? parseInt(e.creditVAT) : null,
                amount: e.amount ? e.amount : null,
                voucherDate: e.voucherDate ? format(new Date(e.voucherDate), "yyyy-MM-dd") : null,
                dueDate: e.dueDate ? format(new Date(e.dueDate), "yyyy-MM-dd") : null,
                valueDate: e.valueDate ? format(new Date(e.valueDate), "yyyy-MM-dd") : null,
                projectId: e.projectId,
                departmentId: e.departmentId,
                text: e.text,
                crossReference: e.crossReference,
                rowNo: e.rowNo,
                cid: e.cid,
                currency: e.currency,
                currencyVismaId: e.currencyVismaId ? e.currencyVismaId : null,
                currencyRate: e.currencyRate ? e.currencyRate : null,
                currencyAmount: e.currencyAmount ? e.currencyAmount : null,
                currencyDate: e.currencyDate ? format(e.currencyDate, "yyyy-MM-dd") : null,
            }
        });

        const result = await service.postSourceDocument(organizationId, sourceDocumentType, comment, fileContents, setLines);

        if (result.hasErrors) {
            return new BusinessLogicResult(undefined, result.errors);
        } else {
            return new BusinessLogicResult(result.value, result.errors);
        }
    }

    public async putSourceDocumentModify(
        organizationId: string, sourceDocumentId: number, comments: string, data: ISourceDocumentGuiModifyModel[]
    ): Promise<BusinessLogicDefaultResult> {

        const service = new SourceDocumentService();

        let newData: ISourceDocumentModifyModel[] = data.map(e => {
            return {
                invoiceNo: e.invoiceNo,
                debitAccount: e.debitAccount ? parseInt(e.debitAccount) : null,
                debitAccountType: e.debitAccountType,
                debitVAT: e.debitVAT ? parseInt(e.debitVAT) : null,
                creditAccount: e.creditAccount ? parseInt(e.creditAccount) : null,
                creditAccountType: e.creditAccountType,
                creditVAT: e.creditVAT ? parseInt(e.creditVAT) : null,
                amount: e.amount ? e.amount : null,
                voucherDate: e.voucherDate ? format(new Date(e.voucherDate), "yyyy-MM-dd") : null,
                dueDate: e.dueDate ? format(new Date(e.dueDate), "yyyy-MM-dd") : null,
                valueDate: e.valueDate ? format(new Date(e.valueDate), "yyyy-MM-dd") : null,
                projectId: e.projectId,
                departmentId: e.departmentId,
                text: e.text,
                crossReference: e.crossReference,
                rowNo: e.rowNo,
                cid: e.cid,
                currency: e.currency,
                currencyVismaId: e.currencyVismaId ? e.currencyVismaId : null,
                currencyRate: e.currencyRate ? e.currencyRate : null,
                currencyAmount: e.currencyAmount ? e.currencyAmount : null,
                currencyDate: e.currencyDate ? format(e.currencyDate, "yyyy-MM-dd") : null,
            }
        });

        const result = await service.putSourceDocumentModify(organizationId, sourceDocumentId, comments, newData);

        if (result.hasErrors) {
            return new BusinessLogicDefaultResult(result.errors);
        } else {
            return new BusinessLogicDefaultResult([]);
        }

    }

    public async getSourceDocumentsFilePage(
        organizationId: string,
        sourceDocumentFileId: number,
        page: number,
        sourceDocumentId: string,
    ): Promise<BusinessLogicResult<Blob | undefined>> {

        const service = new SourceDocumentService();
        const result = await service.getSourceDocumentsFilePage(
            organizationId,
            sourceDocumentFileId,
            page,
            sourceDocumentId
        );

        if (result.hasErrors) {
            return new BusinessLogicResult(undefined, result.errors);
        } else {
            return new BusinessLogicResult(result.value, result.errors);
        }
    };

    public async getSourceDocumentsFile(
        organizationId: string,
        sourceDocumentFileId: number,
        sourceDocumentId: string,
    ): Promise<BusinessLogicResult<Blob | undefined>> {

        const service = new SourceDocumentService();
        const result = await service.getSourceDocumentsFile(
            organizationId,
            sourceDocumentFileId,
            sourceDocumentId
        );

        if (result.hasErrors) {
            return new BusinessLogicResult(undefined, result.errors);
        } else {
            return new BusinessLogicResult(result.value, result.errors);
        }
    };

    public async getSourceDocumentsFileDownloadPDF(
        organizationId: string,
        sourceDocumentFileId: number,
        sourceDocumentId: string,
    ): Promise<BusinessLogicResult<Blob | undefined>> {

        const service = new SourceDocumentService();
        const result = await service.getSourceDocumentsFileDownloadPDF(
            organizationId,
            sourceDocumentFileId,
            sourceDocumentId
        );

        if (result.hasErrors) {
            return new BusinessLogicResult(undefined, result.errors);
        } else {
            return new BusinessLogicResult(result.value, result.errors);
        }
    };

    public async getSourceDocumentTypeStatistics(
        organizationId: string,
    ): Promise<BusinessLogicResult<ISourceDocumentTypeStatisticsGuiModel>> {


        const service = new SourceDocumentService();
        const result = await service.getSourceDocumentTypeStatistics(organizationId);

        const e = result.value;

        const data: ISourceDocumentTypeStatisticsGuiModel = {
            incomingInvoice: e.incomingInvoice,
            outgoingInvoice: e.outgoingInvoice,
            banking: e.banking,
            statement: e.statement,
            journalEntries: e.journalEntries,
            miscellaneous: e.miscellaneous,
            addition: e.addition,
            cash: e.cash,
        };

        return new BusinessLogicResult(data, result.errors);
    }

}